/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import classnames from 'classnames';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';

import { Link } from 'libs/router';

import Entity from 'components/entity/Entity';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import MoreButton from 'components/buttons/more-button/MoreButton';
import NotesModal from 'modules/notes/components/modal/NotesModal';
import Tooltip from 'components/Tooltip';
import SVGIcon from 'components/svg-icon/SVGIcon';
import StatusLabel from 'components/status-label/StatusLabel';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ResponseTimeInfo from 'components/response-time-info/ResponseTimeInfo';
import DeliverableTypeCounters from 'modules/deliverables/components/deliverable-type-counters/DeliverableTypeCounters';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import BulkOfferModal from '../bulk/bulk-offer-modal/BulkOfferModal';
import NotificationDot from 'components/notification-dot/NotificationDot';
import MetricsChannelsBasic from 'components/metrics/channels-basic/MetricsChannelsBasic';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import CreatorTilePriceReco from 'components/creator-tile/price-reco/CreatorTilePriceReco';

import { DELIVERABLE_TYPE_ZALANDO_OUTFIT } from 'utils/deliverables';
import { BULK_OFFER_MODAL_MODE_CREATE } from 'utils/bulk-offer';
import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { getEntityFromProfile } from 'utils/view';
import { canCampaignBeChanged } from 'utils/campaign';
import {
  SUBCAMPAIGN_STATE_DRAFT,
  SUBCAMPAIGN_STATE_TO_LABEL_TYPE,
  getSubcampaignOfferDisplayBudget,
  isOfferSavedAsDraft,
  isQuote,
  isQuoteProvided
} from 'utils/subcampaign';
import { getDisplayDate } from 'utils/date';
import { isInfluencerProfileRejected } from 'utils/profile';
import { getFormattedFollowersValue, getNumFollowers, mapResponseTypeToCategory } from 'utils/influencers';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL,
  getChannelUserNameWithFallback,
  isChannelHasEnoughFollowers
} from 'utils/channels';
import { getFormattedPercentageRate } from 'utils/formatters';
import { hasAnyReleasedPayout } from 'utils/payouts';

import type {
  Campaign,
  CampaignContentType,
  ChannelTypes,
  Channels,
  DeliverablesOverdueBySubcampaignIdLocal,
  ResponseTime,
  Subcampaign,
  SubcampaignInfluencerProfile,
  SubcampaignOfferDeliverablesOverview,
  SubcampaignState
} from 'types';
import type { MetricsChannelsBasicItem } from 'components/metrics/channels-basic/types';

export type SORTING_METRICS_TYPE = 'followers' | 'engagement' | 'minPricePerPost';

applyTranslations(require('./subcampaigns-table.i18n.json'));

export const SORTING_PRIORITY = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE] as const;
export const RESPONSE_TIME_SORTING_PRIORITY = ['quick', 'average', 'slow', 'not_responsive', 'unknown', 'lightweight'];

export const FOLLOWERS = 'followers';
export const ENGAGEMENT = 'engagement';
const MIN_PRICE_PER_POST = 'minPricePerPost';

export const METRICS_MIN = {
  [FOLLOWERS]: MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL,
  [ENGAGEMENT]: 0,
  [MIN_PRICE_PER_POST]: 0
};

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

export type SubcampaignTableActions = {
  onRemoveFromShortlist: (b: Subcampaign, a: Campaign) => void,
  onCopyOfferDetails?: (a: Subcampaign) => void,
  onPasteCopiedOfferDetails?: ((b: Subcampaign, a: Campaign) => void) | null,
  onDownloadOffer: (a: Subcampaign) => void,
  onSpecifyOffer: (b: number, a: number) => void
};

type SubcampaignContextMenuProps = {
  actions: SubcampaignTableActions,
  campaign: Campaign,
  subcampaign: Subcampaign,
  subcampaignState: SubcampaignState
};

export type SubcampaignTableItem = {
  actions: SubcampaignTableActions,
  campaign: Campaign,
  subcampaign: Subcampaign,
  responseTime: ResponseTime | undefined | null,
  variablePaymentAmount: number | undefined | null,
  subcampaignState: SubcampaignState,
  hasUnreadMessages: boolean,
  isSelected?: boolean | null,
  isSelectable: boolean,
  campaignContentType: CampaignContentType,
  taskChannelTypes: Set<ChannelTypes>,
  isMultipleOfferCreationActive: boolean,
  hasDraft?: boolean
};

type CheckboxColumnProps = {
  isChecked: boolean,
  onToggle: (a: number) => void,
  onToggleAll: () => void
};

type CheckboxTitleProps = {
  isChecked: boolean,
  onChange: () => void
};

export const AVATAR_SIZE = {
  width: 24,
  height: 24
};

const POPOVER_AVATAR_SIZE = {
  width: 48,
  height: 48
};

export const CREATOR_NAME_SIZE = {
  maxWidth: 132
};

const POPOVER_CREATOR_NAME_SIZE = {
  maxWidth: 154
};


export function getChannels(
  taskChannelTypes: Set<ChannelTypes>,
  isDraft: boolean,
  onlyOutfitTask: boolean
): Array<string> {
  // show all possible channels for draft/shortlisted offers
  const displayableChannels: Array<ChannelTypes> = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE];
  return displayableChannels.filter(channel => taskChannelTypes.has(channel) || isDraft || onlyOutfitTask);
}

export function getFilteredChannels(
  channels: Channels,
  taskChannelTypes: Set<ChannelTypes>,
  isDraftOffer: boolean,
  onlyOutfitTask?: boolean
): Array<string> {
  return getChannels(
    taskChannelTypes,
    isDraftOffer,
    onlyOutfitTask || Boolean(onlyOutfitTask)
  )
    .filter(channelType => {
      const channel = channels[channelType];

      return channel && (
        // skip followers amount check for non-draft offers since those offers has task of such type added intentionally
        isChannelHasEnoughFollowers(channel) || !isDraftOffer
      );
    });
}

export function containsOnlyZOutfitTask(
  deliverablesOverview: Omit<SubcampaignOfferDeliverablesOverview, 'totalCount'> = {}
): boolean {
  const deliverableKeys = Object.keys(deliverablesOverview)
    .filter(dk => dk !== 'totalCount');

  const nonZalandoOutfitTypes = deliverableKeys
    .filter(key => !isEmpty(deliverablesOverview[key]))
    .reduce((acc, key) => {
      if (snakeCase(key) !== DELIVERABLE_TYPE_ZALANDO_OUTFIT) {
        acc.push(key);
      }

      return acc;
    }, [] as Array<string>);

  return isEmpty(nonZalandoOutfitTypes);
}

export const getCreatorPopoverComponent = (creatorProfile: SubcampaignInfluencerProfile) => {
  const entity = getEntityFromProfile(creatorProfile);
  const entityUrl = `/content-creator/profiles/${entity?.id ?? ''}`;
  const channels: Array<MetricsChannelsBasicItem> = [];

  const showChannels: Array<ChannelTypes> = ['instagram', 'tiktok', 'youtube'];

  showChannels.forEach((channel) => {
    const channelInfo = creatorProfile.channels[channel];
    if (channelInfo) {
      channels.push({
        engagementRate: channelInfo.metrics?.engagement,
        followers: channelInfo.metrics?.followers,
        type: channel,
        url: channelInfo.url
      });
    }
  });

  return (
    <div className="subcampaigns-table__creator-popover">
      <EntityAvatar
        bordered
        customSize={POPOVER_AVATAR_SIZE}
        entity={entity}
        openInNewTab
      />
      <div className="subcampaigns-table__creator-popover-metrics">
        <EntityName
          className="subcampaigns-table__cell--creator"
          maxLength={CREATOR_NAME_SIZE.maxWidth}
          style={POPOVER_CREATOR_NAME_SIZE}
          size="small"
          entity={entity}
          link={entityUrl}
          openInNewTab
          hideTitle
        />
        <VerticalSpacing type="tiny" />
        <MetricsChannelsBasic
          includeRatio
          channels={channels}
          showChannels={['instagram', 'tiktok', 'youtube']}
          layout="block"
        />
      </div>
    </div>
  );
};

const getCreatorColumn = () => ({
  title: __t('subcampaigns_table.columns.creator'),
  key: 'creator',
  width: 180,
  sort: (a: SubcampaignTableItem, b: SubcampaignTableItem) =>
    a.subcampaign.creatorProfile.name.localeCompare(b.subcampaign.creatorProfile.name),
  render: ({
    subcampaign,
    subcampaignState
  }: SubcampaignTableItem) => {
    const { id, campaignId } = subcampaign;
    const entity = getEntityFromProfile(subcampaign.creatorProfile);
    const entityUrl = `/content-creator/profiles/${entity?.id ?? ''}`;

    const classNames = classnames(
      'subcampaigns-table__cell',
      'subcampaigns-table__cell--creator',
      {
        'subcampaigns-table__cell--padded': subcampaignState !== SUBCAMPAIGN_STATE_DRAFT
      }
    );

    const isDraft = subcampaignState === SUBCAMPAIGN_STATE_DRAFT;
    const message = isDraft ? __t('subcampaigns_table.tooltips.creator') : undefined;
    const component = isDraft ? undefined : getCreatorPopoverComponent(subcampaign.creatorProfile);

    return (
      <div className={classNames}>
        <Tooltip
          width={isDraft ? 'normal' : 'auto-compact'}
          message={message}
          component={component}
        >
          <Entity>
            <EntityAvatar
              bordered
              customSize={AVATAR_SIZE}
              entity={entity}
              link={entityUrl}
              openInNewTab
            />
            <EntityName
              maxLength={CREATOR_NAME_SIZE.maxWidth}
              style={CREATOR_NAME_SIZE}
              size="small"
              entity={entity}
              link={entityUrl}
              openInNewTab
              hideTitle
            />
          </Entity>
        </Tooltip>
        {isOfferSavedAsDraft(subcampaign.offer) && (
          <a
            href={`/campaigns/${campaignId}/subcampaigns/${id}/edit?new=1&bulk=create_offers&draft=open`}
            className="subcampaigns-table__cell-draft-link"
            onClick={event => event.stopPropagation()}
            target="_self"
          >
            {__t('subcampaigns_table.draft_link')}
          </a>
        )}
      </div>
    );
  }
});

function renderChannels(
  {
    subcampaign,
    taskChannelTypes
  }: SubcampaignTableItem,
  showUserName: boolean
): React.ReactNode {
  const channels = subcampaign.creatorProfile.channels;

  return (
    <div className="subcampaigns-table__cell-rows">
      {
        getFilteredChannels(
          channels,
          taskChannelTypes,
          subcampaign.state === 'draft',
          containsOnlyZOutfitTask(subcampaign.offer.deliverablesOverview)
        )
          .map(channelType => {
            const channel = channels[channelType];

            let username = getChannelUserNameWithFallback(channel);
            const classes = classnames('subcampaigns-table__cell-row', {
              [`subcampaigns-table__cell-${channelType}-metric`]: true
            });

            if (channelType === CHANNEL_TYPE_INSTAGRAM) {
              username = `@${username}`;
            }

            const channelClasses = classnames('subcampaigns-table__channel', {
              'subcampaigns-table__channel--centered': !showUserName
            });

            return (
              <div
                className={classes}
                key={channelType}
              >
                <div className={channelClasses}>
                  <Tooltip
                    message={__t(`subcampaigns_table.tooltips.channel_${channelType}`)}
                  >
                    <div className="subcampaigns-table__channel-icon">
                      <SVGIcon
                        name={`${channelType}-no-customisation`}
                        width={16}
                        height={16}
                        link={channel.url}
                      />
                    </div>
                  </Tooltip>
                  {
                    showUserName &&
                      <div
                        className="subcampaigns-table__channel-name"
                        title={username}
                      >
                        {username}
                      </div>
                  }
                </div>
              </div>
            );
          })
      }
    </div>
  );
}

const getWideChannelColumn = () => ({
  title: __t('subcampaigns_table.columns.channel'),
  key: 'channels',
  align: 'center',
  width: 114,
  render: (col: SubcampaignTableItem) => renderChannels(col, true)
});

const getChannelColumn = () => ({
  title: __t('subcampaigns_table.columns.channel'),
  key: 'channels',
  align: 'center',
  width: 76,
  render: (col: SubcampaignTableItem) => renderChannels(col, false)
});

const getMetric = (subcampaign: Subcampaign, channel: typeof SORTING_PRIORITY[number], metricType: SORTING_METRICS_TYPE) =>
  (subcampaign.creatorProfile.channels[channel]?.metrics?.[metricType] || 0);

const getSortingByMetrics = (metricType: SORTING_METRICS_TYPE) =>
  (a: SubcampaignTableItem, b: SubcampaignTableItem) => {
    /* eslint-disable no-param-reassign */

    const metrics = SORTING_PRIORITY.reduce((prevValue, channel) => {
      const metricsValueA = getMetric(a.subcampaign, channel, metricType);
      const metricsValueB = getMetric(b.subcampaign, channel, metricType);

      prevValue.metricsValueA[channel] = metricsValueA >= METRICS_MIN[metricType] ? metricsValueA : 0;
      prevValue.metricsValueB[channel] = metricsValueB >= METRICS_MIN[metricType] ? metricsValueB : 0;

      return prevValue;
    }, { metricsValueA: {}, metricsValueB: {} });

    const diff = SORTING_PRIORITY.reduce((prevValue: number, channel) => {
      if ((metrics.metricsValueA[channel] - metrics.metricsValueB[channel]) && !prevValue) {
        return metrics.metricsValueA[channel] - metrics.metricsValueB[channel];
      }

      return prevValue;
    }, 0);

    // In case of metric being 0 for both subcampaign, order will be decided on followers
    return diff === 0 && metricType !== FOLLOWERS ? getSortingByMetrics(FOLLOWERS)(a, b) : diff;
  };

const getFollowersColumn = () => ({
  title: (
    <div className="subcampaigns-table__head-icon">
      <Tooltip
        message={__t('subcampaigns_table.tooltips.followers')}
      >
        <SVGDynamicIcon
          name="users-connected-custom-fg"
          width={16}
          height={16}
          customColor="grey"
        />
      </Tooltip>
    </div>
  ),
  width: 62,
  key: 'followers',
  customizable: true,
  sort: getSortingByMetrics(FOLLOWERS),
  render: ({
    subcampaign,
    taskChannelTypes
  }: SubcampaignTableItem) => {
    const channels = subcampaign.creatorProfile.channels;

    return (
      <div className="subcampaigns-table__cell-rows">
        {
          getFilteredChannels(
            channels,
            taskChannelTypes,
            subcampaign.state === 'draft',
            containsOnlyZOutfitTask(subcampaign.offer.deliverablesOverview)
          )
            .map(channelType => {
              const channel = channels[channelType];

              const metrics = channel.metrics;
              const numFollowers = getNumFollowers(metrics.followers);
              const formattedFollowers = getFormattedFollowersValue(numFollowers);
              const classes = classnames('subcampaigns-table__cell-row', {
                'subcampaigns-table__cell-row--centered': true,
                [`subcampaigns-table__cell-${channelType}-metric`]: true
              });

              return (
                <div
                  className={classes}
                  key={channelType}
                >
                  <div className="subcampaigns-table__metric">
                    {formattedFollowers}
                  </div>
                </div>
              );
            })
        }
      </div>
    );
  }
});

const getEngagementColumn = () => ({
  title: (
    <div className="subcampaigns-table__head-icon">
      <Tooltip
        message={__t('subcampaigns_table.tooltips.engagement')}
      >
        <SVGDynamicIcon
          name="rotation-arrows-custom-fg"
          width={16}
          height={16}
          customColor="grey"
        />
      </Tooltip>
    </div>
  ),
  width: 64,
  key: 'engagement_rate',
  customizable: true,
  sort: getSortingByMetrics(ENGAGEMENT),
  render: ({
    subcampaign,
    taskChannelTypes
  }: SubcampaignTableItem) => {
    const channels = subcampaign.creatorProfile.channels;

    return (
      <div className="subcampaigns-table__cell-rows">
        {
          getFilteredChannels(
            channels,
            taskChannelTypes,
            subcampaign.state === 'draft',
            containsOnlyZOutfitTask(subcampaign.offer.deliverablesOverview)
          )
            .map(channelType => {
              const channel = channels[channelType];

              const metrics = channel.metrics;
              const engagement = getFormattedPercentageRate(metrics.engagement);
              const classes = classnames('subcampaigns-table__cell-row', {
                'subcampaigns-table__cell-row--centered': true,
                [`subcampaigns-table__cell-${channelType}-metric`]: true
              });

              return (
                <div
                  className={classes}
                  key={channelType}
                >
                  <div className="subcampaigns-table__metric">
                    {engagement}
                  </div>
                </div>
              );
            })
        }
      </div>
    );
  }
});

const getPriceRecoColumn = () => ({
  title: __t('subcampaigns_table.columns.price_recommendation'),
  titleTooltipMsg: __t('subcampaigns_table.tooltips.price_recommendation'),
  key: 'price_reco',
  customizable: true,
  width: 342,
  align: 'left',
  sort: getSortingByMetrics(MIN_PRICE_PER_POST),
  render: ({
    subcampaign,
    taskChannelTypes
  }: SubcampaignTableItem) => {
    const channels = subcampaign.creatorProfile.channels;

    return (
      <div className="subcampaigns-table__cell-rows">
        {
          getFilteredChannels(channels, taskChannelTypes, subcampaign.state === 'draft')
            .map(channelType => {
              const classes = classnames(
                'subcampaigns-table__cell-row',
                'subcampaigns-table__price-reco-item', {
                  [`subcampaigns-table__cell-${channelType}-metric`]: true
                }
              );

              return (
                <div
                  key={channelType}
                  className={classes}
                >
                  <CreatorTilePriceReco
                    emptyMessage="–"
                    size="large"
                    channels={[channelType]}
                    channelsData={channels}
                  />
                </div>
              );
            })
        }
      </div>
    );
  }
});

const getResponseTimeColumn = () => ({
  title: __t('subcampaigns_table.columns.response_time'),
  key: 'response_time',
  customizable: true,
  sort: (a: SubcampaignTableItem, b: SubcampaignTableItem, ascending: boolean) => {
    const responseTimeA = (a.subcampaign.creatorProfile.isLightweight)
      ? 'lightweight'
      : mapResponseTypeToCategory(a.responseTime);
    const responseTimeB = (b.subcampaign.creatorProfile.isLightweight)
      ? 'lightweight'
      : mapResponseTypeToCategory(b.responseTime);

    if (!responseTimeA) {
      return 1;
    }

    if (!responseTimeB) {
      return -1;
    }

    const compareIndex = RESPONSE_TIME_SORTING_PRIORITY.indexOf(responseTimeA)
    - RESPONSE_TIME_SORTING_PRIORITY.indexOf(responseTimeB);

    if (compareIndex === 0) {
      return ascending ? getSortingByMetrics(FOLLOWERS)(b, a) : getSortingByMetrics(FOLLOWERS)(a, b);
    }

    return compareIndex;
  },
  render: ({
    subcampaign,
    responseTime
  }: SubcampaignTableItem) => {
    if (responseTime) {
      return (
        <ResponseTimeInfo
          abbreviatedBrandLabel
          creatorProfile={subcampaign.creatorProfile}
          responseTime={responseTime}
          showQuestionMark
        />
      );
    }

    return null;
  }
});

function getContextMenu(
  {
    campaign,
    subcampaign,
    subcampaignState,
    actions
  }: SubcampaignContextMenuProps
) {
  const canEdit = canCampaignBeChanged(campaign);
  const isPendingOrAcceptedSubcampaign = subcampaignState === 'pending' || subcampaignState === 'accepted';
  const visibleOffer = subcampaign.offer || subcampaign.updatedOffer;
  const isCopyableState = isPendingOrAcceptedSubcampaign || subcampaignState === 'revoked' || isOfferSavedAsDraft(visibleOffer);
  const isDraftState = subcampaignState === 'draft';
  const influencer = subcampaign.creatorProfile;
  const isInfluencerRejected = isInfluencerProfileRejected(subcampaign.creatorProfile);
  const entity = getEntityFromProfile(influencer);

  const items = [
    ...insertIf(canEdit && isDraftState, {
      name: 'create_offer',
      text: __t('subcampaigns_table.context_menu.create_offer_label'),
      onClick: () => actions.onSpecifyOffer(campaign.id, subcampaign.id)
    }),
    ...insertIf(isCopyableState && actions.onCopyOfferDetails, {
      name: 'copy_offer_details',
      text: __t('subcampaigns_table.context_menu.copy_offer_details_label'),
      onClick: () => actions.onCopyOfferDetails && actions.onCopyOfferDetails(subcampaign)
    }),
    ...insertIf(canEdit && !isInfluencerRejected && actions.onPasteCopiedOfferDetails, {
      name: 'paste_offer_details',
      text: __t('subcampaigns_table.context_menu.paste_offer_details_label'),
      onClick: () => actions.onPasteCopiedOfferDetails && actions.onPasteCopiedOfferDetails(subcampaign, campaign)
    }),
    ...insertIf(canEdit && isDraftState, {
      name: 'remove_subcampaign',
      text: __t('subcampaigns_table.context_menu.remove_subcampaign_label'),
      onClick: () => actions.onRemoveFromShortlist(subcampaign, campaign)
    }),
    ...insertIf(canEdit && isPendingOrAcceptedSubcampaign, {
      name: 'bulk-offer',
      type: 'modal',
      text: __t('subcampaigns_table.context_menu.bulk_offer_send_label'),
      meta: {
        mode: BULK_OFFER_MODAL_MODE_CREATE,
        campaignId: campaign.id,
        subcampaign,
        sentSubcampaignId: subcampaign.id,
        bulkOffer: {
          offer: visibleOffer,
          shouldLoadDeliverables: true
        }
      },
      extra: {
        component: BulkOfferModal
      }
    }),
    ...insertIf(isPendingOrAcceptedSubcampaign, {
      name: 'download_offer',
      text: __t('subcampaigns_table.context_menu.download_offer_label'),
      onClick: () => actions.onDownloadOffer(subcampaign)
    }),
    ...insertIf(isPendingOrAcceptedSubcampaign && !hasAnyReleasedPayout(subcampaign.payouts), {
      type: 'cancel_offer',
      text: __t('subcampaigns_table.context_menu.cancel_offer_label'),
      meta: {
        subcampaign
      }
    }),
    {
      name: 'profile',
      text: __t('subcampaigns_table.context_menu.profile_label'),
      link: entity?.url,
      openInNewTab: true
    },
    {
      type: 'modal',
      name: 'notes',
      text: __t('subcampaigns_table.context_menu.notes_label'),
      meta: {
        influencer
      },
      extra: {
        component: NotesModal
      }
    },
    ...insertIf(!isInfluencerRejected, {
      type: 'lists',
      name: 'lists',
      text: __t('subcampaigns_table.context_menu.lists_label'),
      meta: {
        influencer
      }
    })
  ];


  return (
    <MoreButtonWithMenu
      width={16}
      contextMenuProps={{ items }}
    />
  );
}

const getActionsColumn = () => ({
  title: __t('subcampaigns_table.columns.actions'),
  key: 'actions',
  width: 174,
  render: (
    {
      actions,
      subcampaignState,
      subcampaign,
      campaign,
      hasUnreadMessages
    }: SubcampaignTableItem
  ) => {
    const canEdit = canCampaignBeChanged(campaign);
    const contextMenu = getContextMenu({ campaign, subcampaign, subcampaignState, actions });
    const isInfluencerRejected = isInfluencerProfileRejected(subcampaign.creatorProfile);

    if (subcampaignState === 'draft' || isInfluencerRejected) {
      return (
        <div className="subcampaigns-table__actions">
          {contextMenu}
        </div>
      );
    }

    const messengerIconClasses = classnames('subcampaigns-table__action', {
      'subcampaigns-table__action--icon': true,
      'subcampaigns-table__action--with-notification': hasUnreadMessages
    });

    return (
      <div className="subcampaigns-table__actions">
        <div className="subcampaigns-table__action subcampaigns-table__action--icon">
          {
            canEdit && (
              <Tooltip
                message={__t('subcampaigns_table.tooltips.edit_offer')}
                width="short"
              >
                <Link to={`/campaigns/${campaign.id}/subcampaigns/${subcampaign.id}/edit`}>
                  <SVGDynamicIcon
                    name="pencil-custom-bg"
                    customColor="grey"
                    clickable
                    width={16}
                    height={16}
                  />
                </Link>
              </Tooltip>
            )
          }
        </div>
        <div className={messengerIconClasses}>
          <Tooltip
            message={__t('subcampaigns_table.tooltips.view_messenger')}
            width="short"
          >
            <Link to={`/campaigns/${campaign.id}/messages?subcampaignId=${subcampaign.id}`}>
              <SVGDynamicIcon
                name="speech-bubble-custom-bg"
                customColor="grey"
                clickable
                width={16}
                height={16}
              />
            </Link>
          </Tooltip>
          {
            hasUnreadMessages &&
            <div className="subcampaigns-table__notification">
              <NotificationDot bordered />
            </div>
          }
        </div>
        {contextMenu}
      </div>
    );
  }
});

const getOfferColumn = () => ({
  title: __t('subcampaigns_table.columns.offer'),
  key: 'offer',
  render: ({
    subcampaign
  }: SubcampaignTableItem) => (
    <div className="subcampaigns-table__cell-rows">
      <div className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered">
        <div className="subcampaigns-table__payment">
          {getSubcampaignOfferDisplayBudget(subcampaign.offer)}
          {
            isQuote(subcampaign) && (isQuoteProvided(subcampaign) ? (
              <span>
                {__t('subcampaigns_table.quote_provided')}
              </span>
            ) : (
              <span>
                {__t('subcampaigns_table.quote_requested')}
              </span>
            )
            )
          }
        </div>
      </div>
    </div>
  )
});

const getReopenedOfferColumn = () => ({
  title: __t('subcampaigns_table.columns.offer_reopened'),
  popoverList: [
    {
      title: __t('subcampaigns_table.tooltips.expires.title'),
      text: __t('subcampaigns_table.tooltips.expires.agreed_reopened')
    }
  ],
  popoverListWidth: 'wide',
  key: 'offer_reopened',
  render: ({
    subcampaign
  }: SubcampaignTableItem) => (
    <div className="subcampaigns-table__cell-rows">
      <div className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered">
        <div className="subcampaigns-table__payment">
          {subcampaign.updatedOffer && getSubcampaignOfferDisplayBudget(subcampaign.updatedOffer)}
        </div>
      </div>
    </div>
  )
});

const getTasksColumn = (
  overdueDeliverables?: DeliverablesOverdueBySubcampaignIdLocal,
  subcampaignOfferSortState?: string
) => ({
  title: __t('subcampaigns_table.columns.tasks'),
  key: 'tasks',
  width: 226,
  render: ({
    subcampaign
  }: SubcampaignTableItem) => (
    <DeliverableTypeCounters
      subcampaignId={subcampaign.id}
      deliverablesOverview={subcampaign.offer.deliverablesOverview}
      overdueDeliverables={overdueDeliverables}
      subcampaignOfferSortState={subcampaignOfferSortState}
    />
  )
});

const getVariablePaymentColumn = () => ({
  title: __t('subcampaigns_table.columns.variable_payment'),
  key: 'variable_payment',
  render: ({
    variablePaymentAmount
  }: SubcampaignTableItem) => (
    <div className="subcampaigns-table__cell-rows">
      <div className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered">
        <div className="subcampaigns-table__payment">
          {renderPrice(variablePaymentAmount, { defaultValue: '' })}
        </div>
      </div>
    </div>
  )
});

const getRevokedReasonColumn = () => ({
  title: __t('subcampaigns_table.columns.revoked_reason'),
  key: 'revoked_reason',
  sort: (a: SubcampaignTableItem, b: SubcampaignTableItem) => {
    const reasonA = a.subcampaign.revokedBy || '';
    const reasonB = b.subcampaign.revokedBy || '';
    return reasonA.localeCompare(reasonB);
  },
  render: ({
    subcampaign
  }: SubcampaignTableItem) => (
    <div className="subcampaigns-table__cell-rows">
      <div className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered">
        <div className="subcampaigns-table__revoked">
          <StatusLabel
            size="tiny"
            label={__t(`subcampaigns_table.revoked_by.${String(subcampaign.revokedBy)}`)}
            type={SUBCAMPAIGN_STATE_TO_LABEL_TYPE.revoked}
          />
        </div>
      </div>
    </div>
  )
});

const getGenderColumn = () => ({
  title: __t('subcampaigns_table.columns.creator_gender'),
  key: 'creator_gender',
  customizable: true,
  width: 78,
  sort: (a: SubcampaignTableItem, b: SubcampaignTableItem, ascending: boolean) => {
    const genderA = a.subcampaign.creatorProfile.gender || '';
    const genderB = b.subcampaign.creatorProfile.gender || '';

    const localeCompare = genderA.localeCompare(genderB);

    if (localeCompare === 0) {
      return ascending ? getSortingByMetrics(FOLLOWERS)(b, a) : getSortingByMetrics(FOLLOWERS)(a, b);
    }

    return localeCompare;
  },
  render: ({
    subcampaign
  }: SubcampaignTableItem) => (
    <div className="subcampaigns-table__cell-rows">
      <div className="subcampaigns-table__cell-row">
        {
          subcampaign.creatorProfile.gender
            ? __t(`subcampaigns_table.gender.${subcampaign.creatorProfile.gender}`)
            : __t('subcampaigns_table.columns.not_specified')
        }
      </div>
    </div>
  )
});

const getCountryColumn = () => ({
  title: __t('subcampaigns_table.columns.creator_country'),
  key: 'creator_country',
  customizable: true,
  width: 78,
  sort: (a: SubcampaignTableItem, b: SubcampaignTableItem, ascending: boolean) => {
    const creatorProfileA = a.subcampaign.creatorProfile;
    const creatorProfileB = b.subcampaign.creatorProfile;

    const countryCodeA = isArray(creatorProfileA.locations) ? creatorProfileA.locations[0]?.countryCode : null;
    const countryCodeB = isArray(creatorProfileB.locations) ? creatorProfileB.locations[0]?.countryCode : null;

    if (!countryCodeA) {
      return 1;
    }

    if (!countryCodeB) {
      return -1;
    }

    const localeCompare = countryCodeA.localeCompare(countryCodeB);

    if (localeCompare === 0) {
      return ascending ? getSortingByMetrics(FOLLOWERS)(b, a) : getSortingByMetrics(FOLLOWERS)(a, b);
    }

    return localeCompare;
  },
  render: ({
    subcampaign
  }: SubcampaignTableItem) => {
    const creatorProfile = subcampaign.creatorProfile;

    if (isArray(creatorProfile.locations)) {
      return (
        <div className="subcampaigns-table__cell-rows">
          <div
            title={creatorProfile.locations[0]?.country}
            className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered"
          >
            {creatorProfile.locations[0]?.countryCode}
          </div>
        </div>
      );
    }

    return null;
  }
});

const getTierColumn = () => ({
  title: __t('subcampaigns_table.columns.creator_tier'),
  key: 'creator_tier',
  align: 'center',
  customizable: true,
  width: 68,
  sort: getSortingByMetrics(FOLLOWERS),
  render: ({
    subcampaign,
    taskChannelTypes
  }: SubcampaignTableItem) => {
    const channels = subcampaign.creatorProfile.channels;
    const tierLabel = subcampaign.creatorProfile.tier
      ? __t(`subcampaigns_table.creator_tier.${subcampaign.creatorProfile.tier}`)
      : '-';
    const placeholder = '\u00A0'; // no-break space

    return (
      <div className="subcampaigns-table__cell-rows">
        {
          getFilteredChannels(channels, taskChannelTypes, subcampaign.state === 'draft')
            .map(channelType => (
              <div
                key={channelType}
                className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered subcampaigns-table__cell-instagram-metric"
              >
                {(channelType === CHANNEL_TYPE_INSTAGRAM) ? tierLabel : placeholder}
              </div>
            ))
        }
      </div>
    );
  }
});

const getExpiresColumn = () => ({
  title: __t('subcampaigns_table.columns.expires'),
  popoverList: [
    {
      title: __t('subcampaigns_table.tooltips.expires.title'),
      text: __t('subcampaigns_table.tooltips.expires.pending')
    }
  ],
  popoverListWidth: 'wide',
  key: 'expires',
  customizable: true,
  width: 90,
  render: ({
    subcampaign
  }: SubcampaignTableItem) => {
    const expirationTimeline = subcampaign.offer.expirationTimeline;

    if (!isNil(expirationTimeline)) {
      return (
        <div className="subcampaigns-table__cell-rows">
          <div className="subcampaigns-table__cell-row subcampaigns-table__cell-row--centered">
            {getDisplayDate(expirationTimeline.expectedExpiredAt)}
          </div>
        </div>
      );
    }

    return null;
  }
});

const SubcampaignsTableCheckboxTitle = ({
  isChecked,
  onChange
}: CheckboxTitleProps) => (
  <div>
    <InputCheckbox
      id={`all-checkbox-${Math.random()}`}
      size="small"
      checked={isChecked || false}
      onChange={onChange}
    />
    {__t('subcampaigns_table.columns.checkbox')}
  </div>
);

export const getCheckboxColumn = ({
  isChecked,
  onToggle,
  onToggleAll
}: CheckboxColumnProps) => ({
  title: (
    <SubcampaignsTableCheckboxTitle
      isChecked={isChecked}
      onChange={onToggleAll}
    />
  ),
  key: 'checkbox',
  width: 65,
  className: 'subcampaigns-table__checkbox-header',
  render: ({
    subcampaign,
    isSelected,
    isSelectable
  }: SubcampaignTableItem) => {
    const tooltip = (!isSelectable)
      ? __t('subcampaigns_table.tooltips.insufficient_channels')
      : undefined;

    return (
      <div
        className="subcampaigns-table__checkbox-container"
        onClick={event => event.stopPropagation()}
      >
        <InputCheckbox
          id={`row-checkbox-${subcampaign.id}-${Math.random()}`}
          size="small"
          tooltipMessage={tooltip}
          disabled={!isSelectable}
          checked={isSelected || false}
          onChange={() => {
            onToggle(subcampaign.id);
          }}
        />
        {
          subcampaign.creatorProfile.isPreferred && (
            <Tooltip
              width="auto-compact"
              message={__t('subcampaigns_table.tooltips.starred_creator')}
              classNameWrapper="subcampaigns-table__preffered-tooltip-wrapper"
            >
              <SVGDynamicIcon
                name="star-custom-bg"
                customColor="yellow"
                width={16}
                height={16}
              />
            </Tooltip>
          )
        }
      </div>
    );
  }
});

export const getColumnsBySubcampaignState = (state: {
  subcampaigns: Array<Subcampaign>,
  subcampaignState: SubcampaignState,
  overdueDeliverables: DeliverablesOverdueBySubcampaignIdLocal,
  variablePaymentSum?: number,
  subcampaignOfferSortState?: string
}) => {
  switch (state.subcampaignState) {
    case 'draft':
      return [
        getCreatorColumn(),
        getGenderColumn(),
        getCountryColumn(),
        getWideChannelColumn(),
        getTierColumn(),
        getFollowersColumn(),
        getEngagementColumn(),
        getPriceRecoColumn(),
        getResponseTimeColumn(),
        {
          ...getActionsColumn(),
          width: 40
        }
      ];
    case 'pending':
      return [
        getCreatorColumn(),
        getCountryColumn(),
        getGenderColumn(),
        getChannelColumn(),
        getFollowersColumn(),
        getEngagementColumn(),
        getOfferColumn(),
        getTasksColumn(state.overdueDeliverables, state.subcampaignOfferSortState),
        getExpiresColumn(),
        getActionsColumn()
      ];
    case 'accepted':
      return [
        getCreatorColumn(),
        getCountryColumn(),
        getChannelColumn(),
        getFollowersColumn(),
        getEngagementColumn(),
        getOfferColumn(),
        ...insertIf(
          state.subcampaigns.some(subcampaign => subcampaign.updatedOffer),
          getReopenedOfferColumn()
        ),
        ...insertIf(
          (state.variablePaymentSum || 0) > 0,
          getVariablePaymentColumn()
        ),
        getTasksColumn(state.overdueDeliverables, state.subcampaignOfferSortState),
        getActionsColumn()
      ];
    case 'revoked':
      return [
        getCreatorColumn(),
        getCountryColumn(),
        getChannelColumn(),
        getFollowersColumn(),
        getEngagementColumn(),
        getOfferColumn(),
        ...insertIf(
          (state.variablePaymentSum || 0) > 0,
          getVariablePaymentColumn()
        ),
        getRevokedReasonColumn(),
        getTasksColumn(undefined, state.subcampaignOfferSortState),
        getActionsColumn()
      ];
    default:
      return [];
  }
};
